import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-plugin-datalabels';

import { FreightPercentageOverTime } from '../../hooks/freights/useGetFreightStatistics';
import {
  Box,
  Card,
  Grid,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { checkAdmin } from '../../services/validation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

type CustomChartPlugin = {
  id: string;
  afterDraw: (chart: any) => void;
};

interface Props {
  data: FreightPercentageOverTime[];
  dataFreights?: any;
}

const useStyles = makeStyles(theme =>
  createStyles({
    boxes: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    card: {
      flex: 1,
      border: '2px solid #fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      backgroundColor: '#fff',
      padding: '20px',
      marginTop: '1rem',
      position: 'relative',
      borderRadius: '0.25rem',
      width: '100%',
      display: 'flex',
      alignItems: 'end',
    },
    graph: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      border: '2px solid #fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      backgroundColor: '#fff',
      padding: '20px',
      marginTop: '1rem',
      position: 'relative',
      borderRadius: '0.25rem',
    },
    title: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: '1.8rem',
      color: '#1c2434',
      lineHeight: '2rem',
      fontWeight: 600,
      marginBottom: 20,
    },
    subTitle: {
      fontFamily: 'Quicksand, sans-serif',
      fontWeight: 500,
      color: '#FF7206',
      fontSize: '1.8rem',
    },
    paragraph: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: '1rem',
      color: '#64748B',
      fontWeight: 500,

      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
);
const Charts: React.FC<Props> = ({ data, dataFreights }) => {
  const token = useSelector(
    (state: ApplicationState) => state.auth.token || state.distributor.token,
  );

  const classes = useStyles();
  const isLargeScreen = useMediaQuery('(min-width: 1345px)');

  const parsedData = {
    labels: data.map(item => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Custo percentual do frete ',
        data: data.map(item => Math.min(item.averagePercentageDaily, 100)),
        backgroundColor: '#FF7206',
        barThickness: 25,
        borderRadius: 5,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        max: 100,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Porcentagem (%)',
        },
      },
      x: {
        title: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
  };

  const plugins: CustomChartPlugin[] = [
    {
      id: 'custom-plugin',
      afterDraw: (chart: any) => {
        const ctx = chart.ctx;
        chart.data.datasets.forEach((dataset: any, dataIndex: any) => {
          const meta = chart.getDatasetMeta(dataIndex);
          if (!meta.hidden) {
            meta.data.forEach((bar: any, index: any) => {
              const data = dataset.data[index];
              const x = bar.x;
              const y = bar.y;
              const text = data.toFixed(2) + '%';

              ctx.font = '12px Raleway, sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillStyle = '#1c2434';

              ctx.fillText(text, x, Math.max(y - 10, 15));
            });
          }
        });
      },
    },
  ];

  return (
    <>
      {checkAdmin(token) ? (
        <Grid item xs={12} md={12} lg={12}>
          <Card className={classes.graph}>
            <Typography className={classes.title}>Gráfico de Custo</Typography>
            <Bar options={options} data={parsedData} plugins={plugins} />
          </Card>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            md={isLargeScreen ? 9 : 12}
            lg={isLargeScreen ? 9 : 12}
          >
            <Card className={classes.graph}>
              <Typography className={classes.title}>
                Gráfico de Custo
              </Typography>
              <Bar options={options} data={parsedData} plugins={plugins} />
            </Card>
          </Grid>
          {isLargeScreen && (
            <Grid item xs={12} md={3} lg={3} className="CardCusto">
              <Box className={classes.boxes}>
                <Box className={classes.card}>
                  <Grid item xs={12}>
                    <Typography className={classes.subTitle}>
                      {Number(dataFreights?.averagePercentage).toFixed(2) ||
                        'Sem dados'}
                    </Typography>
                    <Typography className={classes.paragraph}>
                      Custo de frete (%)
                    </Typography>
                  </Grid>
                </Box>
                <Box className={classes.card}>
                  <Grid item xs={12}>
                    <Typography className={classes.subTitle}>
                      {Number(dataFreights?.averageFreightDaily).toFixed(2) ||
                        'Sem dados'}
                    </Typography>
                    <Typography className={classes.paragraph}>
                      Custo de frete por dia (%)
                    </Typography>
                  </Grid>
                </Box>
                <Box className={classes.card}>
                  <Grid item xs={12}>
                    <Typography className={classes.subTitle}>
                      {dataFreights?.freightCount || 'Sem dados'}
                    </Typography>
                    <Typography className={classes.paragraph}>
                      Quantidade de fretes
                    </Typography>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default Charts;
